import { Search } from 'lucide-react'

import { useState } from 'react'

import { Badge, Input } from '@/components/ui'
import { Pager } from '@/components/pos/office/Pager'
import { NativeSelect } from '@/components/pos/office/NativeSelect'
import { NeedsConnection } from '@/components/pos/office/NeedsConnection'
import { OfficeTable } from '@/components/pos/office/OfficeTable'
import { useBranchTerminals, useOfficeQuery, useOnline } from '@/hooks/features/office'
import { useDebounce } from '@/hooks/ui/useDebounce'
import { OfficeModel } from '@/http/models'
import { formatDateTime, formatMoney } from '@/utils'

const STATUSES = [
  { value: '', label: 'All statuses' },
  { value: 'completed', label: 'Completed' },
  { value: 'partially_returned', label: 'Partly returned' },
  { value: 'returned', label: 'Returned' },
  { value: 'cancelled', label: 'Cancelled' },
]

/**
 * Every POS sale of this branch on the server, beyond the days the device holds (B15). Read-only: returns and cancels
 * work on the device's own orders (the other tab), so they sync like any till write.
 */
export const OrderHistory = () => {
  const online = useOnline()
  const { data: terminals = [] } = useBranchTerminals()
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [terminal, setTerminal] = useState('')
  const [page, setPage] = useState(1)
  const debounced = useDebounce(search)
  const terminalIds = terminal || terminals.map((t) => String(t._id)).join(',')
  const { data, isFetching } = useOfficeQuery(
    ['orders', terminalIds, debounced, status, page],
    () => OfficeModel.orders({ terminal_id: terminalIds, page, search: debounced || undefined, status: status || undefined }),
    !!terminalIds
  )
  if (!online) return <NeedsConnection />
  const count = data?.count ?? 0
  const nameOf = (id: unknown) => terminals.find((t) => String(t._id) === String(id))

  return (
    <div className="min-h-0 flex-1 space-y-3 overflow-auto p-4 md:p-6">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-64">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => (setSearch(e.target.value), setPage(1))} placeholder="Order, customer or phone" onCameraScan={(code) => (setSearch(code), setPage(1))} className="h-11 pl-9" />
        </div>
        <NativeSelect className="w-44" value={status} onChange={(e) => (setStatus(e.target.value), setPage(1))} options={STATUSES} />
        <NativeSelect
          className="w-44"
          value={terminal}
          onChange={(e) => (setTerminal(e.target.value), setPage(1))}
          options={[{ value: '', label: 'All terminals' }, ...terminals.map((t) => ({ value: String(t._id), label: `T${t.unique_no} · ${t.name}` }))]}
        />
        <span className="text-xs text-muted-foreground">{count} sales</span>
      </div>
      <OfficeTable
        cards
        loading={isFetching}
        rows={data?.result ?? []}
        rowKey={(o) => String(o._id)}
        columns={[
          { label: 'Order', cell: (o) => <span className="font-medium text-ink">{o.order_ref}</span> },
          { label: 'Date', cell: (o) => <span className="tabular-nums text-text-2">{formatDateTime(o.created_at)}</span> },
          { label: 'Terminal', className: 'hidden md:table-cell', cell: (o) => (nameOf(o.terminal_id) ? `T${nameOf(o.terminal_id)!.unique_no}` : '') },
          { label: 'Customer', className: 'hidden md:table-cell', cell: (o) => o.customer_info?.name ?? 'Walk-in' },
          { label: 'Payment', className: 'hidden lg:table-cell capitalize', cell: (o) => o.payment_method },
          { label: 'Total', className: 'text-right', cell: (o) => <span className="tabular-nums text-ink">{formatMoney(Number(o.total ?? 0))}</span> },
          {
            label: 'Status',
            cell: (o) => (
              <Badge tone={o.status === 'completed' ? 'success' : o.status === 'cancelled' ? 'error' : 'warning'} className="capitalize">
                {String(o.status).replace('_', ' ')}
              </Badge>
            ),
          },
        ]}
      />
      <Pager page={page} count={count} onChange={setPage} />
    </div>
  )
}
